import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false,
  }
});

// ── TEAM ──────────────────────────────────────────────────────────────────────
export async function getTeam() {
  const { data, error } = await supabase.from('team').select('*').order('created_at');
  if (error) throw error;
  return data;
}
export async function addTeamMember(member) {
  const { data, error } = await supabase.from('team').insert([member]).select().single();
  if (error) throw error;
  return data;
}
export async function deleteTeamMember(id) {
  const { error } = await supabase.from('team').delete().eq('id', id);
  if (error) throw error;
}

// ── MATTERS ───────────────────────────────────────────────────────────────────
export async function getMatters() {
  const { data, error } = await supabase
    .from('matters')
    .select(`*, tasks(*, subtasks(*), task_notes(*))`)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}
export async function addMatter(matter) {
  const { data, error } = await supabase.from('matters').insert([matter]).select().single();
  if (error) throw error;
  return data;
}
export async function updateMatter(id, patch) {
  // Strip nested relation fields before sending to Supabase
  const { tasks, matter_notes, ...cleanPatch } = patch;
  const { data, error } = await supabase
    .from('matters')
    .update(cleanPatch)
    .eq('id', id)
    .select('id, name, client, type, owner_id, status, priority, start_date, deadline, description, client_email')
    .single();
  if (error) throw error;
  return data;
}
export async function deleteMatter(id) {
  const { error } = await supabase.from('matters').delete().eq('id', id);
  if (error) throw error;
}

// ── MATTER NOTES ──────────────────────────────────────────────────────────────
export async function getMatterNotes(matterId) {
  const { data, error } = await supabase.from('matter_notes').select('*').eq('matter_id', matterId).order('created_at');
  if (error) throw error;
  return data;
}
export async function addMatterNote(note) {
  const { data, error } = await supabase.from('matter_notes').insert([note]).select().single();
  if (error) throw error;
  return data;
}
export async function deleteMatterNote(id) {
  const { error } = await supabase.from('matter_notes').delete().eq('id', id);
  if (error) throw error;
}

// ── TASKS ─────────────────────────────────────────────────────────────────────
export async function addTask(task) {
  const { data, error } = await supabase.from('tasks').insert([task]).select().single();
  if (error) throw error;
  return data;
}
export async function updateTask(id, patch) {
  // Remove any nested/relation fields that could confuse PostgREST
  const { subtasks, task_notes, matterName, matter_id, ...cleanPatch } = patch;
  const { data, error } = await supabase
    .from('tasks')
    .update(cleanPatch)
    .eq('id', id)
    .select('id, title, description, assignees, priority, status, deadline, estimated, spent, matter_id')
    .single();
  if (error) throw error;
  return data;
}
export async function deleteTask(id) {
  const { error } = await supabase.from('tasks').delete().eq('id', id);
  if (error) throw error;
}

// ── TASK NOTES ────────────────────────────────────────────────────────────────
export async function addTaskNote(note) {
  const { data, error } = await supabase.from('task_notes').insert([note]).select().single();
  if (error) throw error;
  return data;
}
export async function deleteTaskNote(id) {
  const { error } = await supabase.from('task_notes').delete().eq('id', id);
  if (error) throw error;
}

// ── DOCUMENTS ─────────────────────────────────────────────────────────────────
export async function getDocuments(linkedType, linkedId) {
  const { data, error } = await supabase
    .from('documents')
    .select('*')
    .eq('linked_type', linkedType)
    .eq('linked_id', linkedId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

// ── NOTIFICATIONS ─────────────────────────────────────────────────────────────
export async function getNotifications(recipientId) {
  const { data, error } = await supabase.from('notifications').select('*')
    .eq('recipient_id', recipientId)
    .order('created_at', { ascending: false })
    .limit(30);
  if (error) throw error;
  return data;
}
export async function createNotification(notif) {
  const { error } = await supabase.from('notifications').insert([notif]);
  if (error) console.error('Notification error:', error);
}
export async function markNotificationRead(id) {
  await supabase.from('notifications').update({ is_read: true }).eq('id', id);
}
export async function markAllNotificationsRead(recipientId) {
  await supabase.from('notifications').update({ is_read: true })
    .eq('recipient_id', recipientId).eq('is_read', false);
}

// ── TEMPLATES ─────────────────────────────────────────────────────────────────
export async function getTemplates() {
  const { data, error } = await supabase.from('templates').select('*').order('category').order('file_name');
  if (error) throw error;
  return data;
}
export async function addTemplate(template) {
  const { data, error } = await supabase.from('templates').insert([template]).select().single();
  if (error) throw error;
  return data;
}
export async function deleteTemplate(id) {
  const { error } = await supabase.from('templates').delete().eq('id', id);
  if (error) throw error;
}

// ── MESSAGING ─────────────────────────────────────────────────────────────────
export async function getChannels() {
  const { data, error } = await supabase.from('channels').select('*').order('created_at');
  if (error) throw error;
  return data;
}
export async function getOrCreateDirect(memberIds) {
  // Look for existing direct channel with exactly these two members
  const { data } = await supabase.from('channels').select('*').eq('type', 'direct');
  const existing = (data || []).find(c =>
    c.members && c.members.length === 2 &&
    memberIds.every(id => c.members.includes(id))
  );
  if (existing) return existing;
  const { data: created, error } = await supabase.from('channels')
    .insert([{ type: 'direct', members: memberIds }]).select().single();
  if (error) throw error;
  return created;
}
export async function getMessages(channelId, limit = 50) {
  const { data, error } = await supabase.from('messages').select('*')
    .eq('channel_id', channelId)
    .order('created_at', { ascending: true })
    .limit(limit);
  if (error) throw error;
  return data;
}
export async function sendMessage(msg) {
  const { data, error } = await supabase.from('messages').insert([msg]).select().single();
  if (error) throw error;
  return data;
}
export async function markRead(channelId, memberId) {
  const { error } = await supabase.from('message_reads')
    .upsert([{ channel_id: channelId, member_id: memberId, last_read: new Date().toISOString() }],
      { onConflict: 'channel_id,member_id' });
  if (error) console.error('markRead error:', error);
}
export async function getUnreadCounts(memberId, channels) {
  if (!channels.length) return {};
  const { data } = await supabase.from('message_reads').select('*').eq('member_id', memberId);
  const reads = {};
  (data || []).forEach(r => { reads[r.channel_id] = r.last_read; });
  const counts = {};
  for (const ch of channels) {
    const lastRead = reads[ch.id] || '1970-01-01';
    const { count } = await supabase.from('messages').select('*', { count:'exact', head:true })
      .eq('channel_id', ch.id).gt('created_at', lastRead);
    counts[ch.id] = count || 0;
  }
  return counts;
}

// ── MEETINGS ──────────────────────────────────────────────────────────────────
export async function getMeetings() {
  const { data, error } = await supabase
    .from('meetings')
    .select('*')
    .order('meeting_date', { ascending: true })
    .order('start_time', { ascending: true });
  if (error) throw error;
  return data;
}
export async function addMeeting(meeting) {
  const { data, error } = await supabase.from('meetings').insert([meeting]).select().single();
  if (error) throw error;
  return data;
}
export async function updateMeeting(id, patch) {
  const { data, error } = await supabase.from('meetings').update(patch).eq('id', id).select().single();
  if (error) throw error;
  return data;
}
export async function deleteMeeting(id) {
  const { error } = await supabase.from('meetings').delete().eq('id', id);
  if (error) throw error;
}

// ── SUBTASKS ──────────────────────────────────────────────────────────────────
export async function addSubtask(subtask) {
  const { data, error } = await supabase.from('subtasks').insert([subtask]).select().single();
  if (error) throw error;
  return data;
}
export async function updateSubtask(id, patch) {
  const { data, error } = await supabase.from('subtasks').update(patch).eq('id', id).select().single();
  if (error) throw error;
  return data;
}
export async function deleteSubtask(id) {
  const { error } = await supabase.from('subtasks').delete().eq('id', id);
  if (error) throw error;
}
